import { useState } from "react";
import { ApiLogEntry } from "../lib/types";
import { toCurl } from "../lib/cURL";

type Props = {
  log: ApiLogEntry[];
  open: boolean;
  onToggle: () => void;
  onClear: () => void;
};

export default function RequestLog({ log, open, onToggle, onClear }: Props) {
  return (
    <div className="border-t border-slate-200 bg-white">
      <div className="px-4 py-2 flex items-center gap-3 text-sm">
        <button onClick={onToggle} className="font-semibold text-slate-700">
          Request log {open ? "▾" : "▸"} <span className="text-slate-400 font-normal">({log.length})</span>
        </button>
        <button
          className="text-xs text-slate-500 hover:text-slate-800"
          onClick={onClear}
          disabled={log.length === 0}
        >
          Clear
        </button>
      </div>
      {open && (
        <div className="max-h-64 overflow-y-auto log-scroll border-t border-slate-100">
          {log.length === 0 && (
            <div className="p-4 text-sm text-slate-500">No API calls yet. Make a request above to see it logged here.</div>
          )}
          {log.map(entry => (
            <LogRow key={entry.id} entry={entry} />
          ))}
        </div>
      )}
    </div>
  );
}

function LogRow({ entry }: { entry: ApiLogEntry }) {
  const [expanded, setExpanded] = useState(false);
  const statusColour = statusColor(entry.status);
  const time = new Date(entry.at).toLocaleTimeString();

  async function copyCurl() {
    try {
      await navigator.clipboard.writeText(toCurl(entry));
    } catch {
      alert("Clipboard unavailable in this browser.");
    }
  }

  return (
    <div className="border-b border-slate-100 px-4 py-2 text-xs">
      <div className="flex items-center gap-3 mono">
        <span className="text-slate-400">{time}</span>
        <span className="font-semibold">{entry.method}</span>
        <span className="text-slate-600 truncate" title={entry.url}>{shortenUrl(entry.url)}</span>
        <span className={`font-semibold ${statusColour}`}>{entry.status ?? "—"}</span>
        {entry.durationMs !== null && <span className="text-slate-400">{entry.durationMs}ms</span>}
        <button className="ml-auto text-brand-700 hover:underline" onClick={copyCurl}>
          Copy cURL
        </button>
        <button className="text-slate-500 hover:text-slate-800" onClick={() => setExpanded(v => !v)}>
          {expanded ? "Hide" : "Expand"}
        </button>
      </div>
      {expanded && (
        <div className="mt-2 grid md:grid-cols-2 gap-2">
          <div>
            <div className="text-slate-500 mb-1">Request</div>
            <pre className="mono bg-slate-50 border border-slate-200 rounded p-2 overflow-auto max-h-40">
{entry.multipartSummary
  ? `multipart: ${entry.multipartSummary}`
  : entry.requestBody ?? "(no body)"}
            </pre>
          </div>
          <div>
            <div className="text-slate-500 mb-1">Response</div>
            <pre className="mono bg-slate-50 border border-slate-200 rounded p-2 overflow-auto max-h-40">
{prettyJson(entry.responseBody) ?? entry.error ?? "(empty)"}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

function statusColor(status: number | null): string {
  if (status === null) return "text-slate-500";
  if (status >= 200 && status < 300) return "text-emerald-600";
  if (status >= 300 && status < 400) return "text-sky-600";
  if (status >= 400 && status < 500) return "text-amber-600";
  return "text-red-600";
}

function shortenUrl(url: string): string {
  try {
    const u = new URL(url);
    return `${u.host}${u.pathname}${u.search}`;
  } catch {
    return url;
  }
}

function prettyJson(s: string | null): string | null {
  if (!s) return null;
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}
